import { createClient, User } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase URL ve Anon Key tanımlanmalıdır');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'kanal-rehberi-auth',
  },
  global: {
    headers: {
      'x-client-info': 'kanal-rehberi',
    },
  },
});

// Ensure profile exists - trigger will handle creation, this is just a fallback
export const ensureProfile = async (user: User) => {
  try {
    console.log('🔄 [ensureProfile] Profil kontrol ediliyor:', user.id);

    const { data: existingProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (fetchError) {
      console.error('❌ [ensureProfile] Profil fetch hatası:', fetchError);
    }

    if (existingProfile) {
      console.log('✅ [ensureProfile] Profil zaten mevcut:', existingProfile.username);
      return existingProfile;
    }

    const username = user.email?.split('@')[0] || `user_${user.id.slice(0, 8)}`;

    // IMPORTANT: Sadece zorunlu alanları set et, diğerleri DB'de default değerlere sahip
    const newProfile = {
      id: user.id,
      username,
      email: user.email || null,
      bio: null,
      avatar: null,
      avatar_frame: 'none',
    };

    console.log('🔄 [ensureProfile] Yeni profil oluşturuluyor:', username);

    const { data: insertedProfile, error: insertError } = await supabase
      .from('profiles')
      .insert(newProfile)
      .select()
      .single();

    if (insertError) {
      console.error('❌ [ensureProfile] Profil insert hatası:', insertError);

      // RLS/Duplicate durumunda tekrar fetch dene
      if (insertError.code === '42501' || insertError.code === '23505') {
        console.log('⚠️ [ensureProfile] RLS/Duplicate, profil tekrar kontrol ediliyor...');

        const { data: retryProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (retryProfile) {
          console.log('✅ [ensureProfile] Profil bulundu (retry):', retryProfile.username);
          return retryProfile;
        }
      }

      console.warn('⚠️ [ensureProfile] Fallback profil kullanılıyor');
      return {
        ...newProfile,
        is_banned: false,
        verified: false,
        role: 'user' as const,
        is_premium: false,
        premium_until: null,
        created_at: new Date().toISOString(),
      };
    }

    console.log('✅ [ensureProfile] Yeni profil oluşturuldu:', insertedProfile?.username);
    return insertedProfile || {
      ...newProfile,
      is_banned: false,
      verified: false,
      role: 'user' as const,
      is_premium: false,
      premium_until: null,
      created_at: new Date().toISOString(),
    };
  } catch (error) {
    console.error('❌ [ensureProfile] Beklenmeyen hata:', error);

    return {
      id: user.id,
      username: user.email?.split('@')[0] || `user_${user.id.slice(0, 8)}`,
      email: user.email || null,
      bio: null,
      avatar: null,
      avatar_frame: 'none',
      is_banned: false,
      verified: false,
      role: 'user' as const,
      is_premium: false,
      premium_until: null,
      created_at: new Date().toISOString(),
    };
  }
};

// Auth helper functions
export const authHelpers = {
  signUp: async (email: string, password: string, username: string) => {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { username },
      },
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error('Kullanıcı oluşturulamadı');

    try {
      await ensureProfile(authData.user);
    } catch (profileError) {
      console.error('Profil kontrol hatası:', profileError);
    }

    return authData;
  },

  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  getCurrentUser: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  ensureProfile: async (_userId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Kullanıcı bulunamadı');
    return ensureProfile(user);
  },

  getProfile: async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, email, bio, avatar, avatar_frame, role, is_premium, premium_until, is_banned, verified, created_at, updated_at')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data;
  },
};
